import React, { useEffect, useState } from 'react';
import { Bullet } from 'components';
import { Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import Image from 'components/Image';
import { isEmpty, shuffle } from 'lodash';
import VideoPlayer from 'components/VideoPlayer';
import { ICategory, ITag } from 'pages/UploadManagement/types';
import { PreviewCotainerProps } from './types';
import { imageMap } from '.images';

const skeletonList = [
  { width: 60, color: '#CCF3F0' },
  { width: 42, color: '#FBEBBC' },
  { width: 63, color: '#CCF3F0' },
  { width: 62, color: '#FBEBBC' },
  { width: 59, color: '#CCF3F0' },
  { width: 60, color: '#CCF3F0' },
  { width: 42, color: '#FBEBBC' },
  { width: 63, color: '#CCF3F0' },
  { width: 53, color: '#F8D6E0' },
  { width: 60, color: '#CCF3F0' },
  { width: 42, color: '#FBEBBC' },
  { width: 63, color: '#CCF3F0' },
  { width: 62, color: '#FBEBBC' }
];

const PreviewCotainer = ({
  conversionType,
  type,
  title,
  description,
  icon,
  iconName,
  redirectLink,
  showAssetFields,
  tags,
  video,
  property,
  category
}: PreviewCotainerProps) => {
  const [showTagSkeletons, setShowTagSkeletons] = useState(true);

  useEffect(() => {
    if (tags) {
      setShowTagSkeletons(Object.values(tags).flat().length === 0);
    }
  }, [tags]);

  return (
    <Box sx={{ height: '78vh', overflow: 'auto' }}>
      <Box pl={8} sx={{ display: 'flex', alignItems: 'flex-end', gap: '16px', position: 'relative' }}>
        {video == null || isEmpty(video) ? (
          <img src={imageMap['conversion-image']()} />
        ) : (
          <VideoPlayer style={{ width: '250px', height: '450px' }} link={video} />
        )}
        {property?.type == 'BAR' && (
          <Button
            size="medium"
            style={{
              width: '238px',
              paddingLeft: '8px',
              background: 'linear-gradient(to right, ' + property?.leftColor + ', ' + property?.rightColor + ')',
              justifyContent: 'start',
              color: property?.textColor || '#FFFFFF',
              position: 'absolute',
              marginBottom: '18px',
              marginLeft: '6px'
            }}
          >
            {property.showIcon && <Image image={icon} width={32} color={property?.iconColor} />}
            <Typography variant="subtitle1" sx={{ marginLeft: '8px' }}>
              {iconName || <Skeleton variant="rounded" width={64} height={12} />}
            </Typography>
          </Button>
        )}
        {property?.type == 'SQUARE' && (
          <Stack direction="column" alignItems="center" spacing={1}>
            {/* <img src={imageMap['preview-img']()} width={40} /> */}
            {icon ? <Image image={icon} width={40} /> : <Skeleton variant="rounded" width={40} height={40} />}

            {iconName ? (
              <Typography variant="caption">{iconName}</Typography>
            ) : (
              <Skeleton variant="rounded" width={64} height={12} />
            )}
          </Stack>
        )}
      </Box>

      <Box pt={2}>
        {/* <Typography
          variant="caption"
          noWrap={true}
          color="GrayText"
          sx={{
            width: 190,
            display: 'flex',
            alignItems: 'center',
            textTransform: 'capitalize'
          }}
        >
          {conversionType ? conversionType.toLowerCase() : <Skeleton variant="rounded" width={50} height={10} />}{' '}
          <Bullet /> {type ? type.toLowerCase() : <Skeleton variant="rounded" width={50} height={10} />}
        </Typography> */}

        {title ? (
          <Typography variant="subtitle1">{title}</Typography>
        ) : (
          <Skeleton variant="rounded" width={250} height={16} />
        )}

        {description ? (
          <Typography variant="caption" sx={{ opacity: '.6' }}>
            {description}
          </Typography>
        ) : (
          <Stack spacing={1} marginTop={1}>
            <Skeleton variant="rounded" width={'100%'} sx={{ maxWidth: '600px' }} height={12} />
            <Skeleton variant="rounded" width={'100%'} sx={{ maxWidth: '600px' }} height={12} />
            <Skeleton variant="rounded" width={240} height={12} />
          </Stack>
        )}

        {redirectLink ? (
          <Typography
            component="div"
            variant="caption"
            sx={{ color: '#833CE2', whiteSpace: 'wrap', wordBreak: 'break-all' }}
          >
            {redirectLink}
          </Typography>
        ) : (
          <>
            {showAssetFields && conversionType == 'redirect' ? (
              <Stack spacing={1} marginTop={1}>
                <Skeleton
                  variant="rounded"
                  width={'100%'}
                  sx={{ maxWidth: '600px', backgroundColor: '#CCCCF1' }}
                  height={12}
                />
              </Stack>
            ) : (
              <></>
            )}
          </>
        )}

        {showAssetFields && showTagSkeletons && (
          <Box
            sx={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginTop: '8px',
              marginBottom: '8px'
            }}
          >
            {shuffle(skeletonList).map((value: any, index: number) => (
              <Skeleton
                key={index}
                variant="rounded"
                width={value.width}
                height={24}
                sx={{ backgroundColor: value.color, borderRadius: '12px' }}
              />
            ))}
          </Box>
        )}

        {tags && <TagCategoryPreview category={category} categoryId={'Tags'} tags={tags['Tags']} />}
        {tags &&
          Object.keys(tags).map(categoryId => {
            if (categoryId === 'Tags') {
              return <React.Fragment key={categoryId}></React.Fragment>;
            } else {
              return (
                <TagCategoryPreview
                  key={categoryId}
                  category={category}
                  categoryId={categoryId}
                  tags={tags[categoryId]}
                />
              );
            }
          })}
      </Box>
    </Box>
  );
};

const TagCategoryPreview = ({
  category,
  categoryId,
  tags
}: {
  category: ICategory[] | undefined;
  categoryId: string;
  tags: (ITag | string)[];
}) => {
  const getCategoryName = (categoryId: string): string => {
    let categoryName = 'Tags';
    if (categoryId !== 'Tags' && category) {
      const existingCategory = category.find((obj: ICategory) => obj.id === categoryId);
      if (existingCategory) {
        categoryName = existingCategory.name;
      }
    }
    return categoryName;
  };

  return tags && tags.length > 0 ? (
    <Box key={categoryId}>
      <Stack>
        <Typography variant="subtitle1" sx={{ marginTop: '8px', marginBottom: '8px' }}>
          {getCategoryName(categoryId)}
        </Typography>
        <Box sx={{ gap: '8px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
          {tags.map((value: ITag | string, index: number) => {
            if (typeof value == 'string') {
              return <Chip sx={{ m: '4px' }} key={index} label={value} />;
            } else {
              return <Chip sx={{ m: '4px', backgroundColor: value.color }} key={index} label={value.name} />;
            }
          })}
        </Box>
      </Stack>
    </Box>
  ) : (
    <></>
  );
};

export default PreviewCotainer;
